import { makeDebug } from '@ps/functions/utils/debug';
import { signInWithCustomToken } from 'firebase/auth';

import { auth } from '../firebase/auth';
import { proxyFetch } from '../utils/proxyFetch';
import { getRecaptchaToken } from '../utils/recaptcha';

const debug = makeDebug('guest-auth');

/**
 * Create a silent guest account.
 * 1. Get reCAPTCHA token
 * 2. Call userApiControllerCreateGuestUser
 * 3. Sign in with the returned custom token
 * Returns the guest user's UID.
 */
export async function createGuestAccount(): Promise<string> {
  // If already signed in as a guest, reuse that account
  const authInstance = auth();
  const currentUser = authInstance.currentUser;
  if (currentUser) {
    const tokenResult = await currentUser.getIdTokenResult();
    if (tokenResult.claims.isGuest) {
      debug('Reusing existing guest account: %s', currentUser.uid);
      return currentUser.uid;
    }
  }

  debug('Creating guest account...');

  const recaptchaToken = await getRecaptchaToken('guest_account_creation');
  debug('reCAPTCHA token obtained');

  const randomId = Math.random().toString(36).slice(2, 10);
  const response = await proxyFetch<{ token: string }>(
    '/api/bio/guest-account',
    {
      method: 'POST',
      body: {
        firstName: 'Anonymous',
        lastName: 'User',
        email: `${randomId}@psl-anonymous.live`,
        recaptchaToken,
      },
    },
  );

  debug.success('Guest account created, signing in...');

  const credential = await signInWithCustomToken(authInstance, response.token);

  debug.success('Guest signed in: %s', credential.user.uid);
  return credential.user.uid;
}

/**
 * Check if a phone number is already associated with an existing account.
 */
export async function checkPhoneConflict(
  phone: string,
): Promise<{ exists: boolean }> {
  try {
    const params = new URLSearchParams({ phone });
    return await proxyFetch<{ exists: boolean }>(
      `/api/bio/user/phone?${params.toString()}`,
    );
  } catch {
    return { exists: false };
  }
}
